/**
 * @file Renders a user's name, avatar and disabled state from the org
 * directory, by id.
 */
import type { ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import { WarningOutlined } from "@ant-design/icons";
import { Typography, Space, Avatar, Tooltip, theme } from "antd";
import type { AvatarProps } from "antd";

import { ROLES, ROLES_MAP } from "@components/Domain";

import getColors from "./helpers/getColors";

const { Text, Link: TextLink } = Typography;

const ROOT = "USR_ROOT";
const GUEST = "USR_GUEST";
const SYSTEM = "SYSTEM";

type PersonName = {
  givenName?: string;
  additionalName?: string;
  familyName?: string;
};

type DirectoryIdentity = PersonName & {
  status?: string;
  isDisabled?: boolean;
  disableReason?: string;
  roles?: string[];
  investor?: PersonName;
};

type OutletContext = {
  membersMap: Record<string, DirectoryIdentity | undefined>;
  brokersMap: Record<string, DirectoryIdentity | undefined>;
  accountsMap?: Record<string, DirectoryIdentity | undefined>;
};

type Props = {
  size?: AvatarProps["size"];
  strong?: boolean;
  onClick?: () => void;
  children?: ReactNode;
  hasAvatar?: boolean;
  shouldFallbackToId?: boolean;
  id: string;
};

/**
 * Looks up the given id in the directory and renders its name, avatar and
 * disabled state, or a guest, system or fallback label when it does not
 * resolve to a directory member.
 */
const Identity = ({
  size = "small",
  strong = false,
  onClick = undefined,
  children = undefined,
  hasAvatar = true,
  shouldFallbackToId = false,
  id,
}: Props): JSX.Element | null => {
  const { token: { colorBgLayout } } = theme.useToken();
  const { membersMap, brokersMap, accountsMap = {} } = useOutletContext<OutletContext>();

  const isLink = !!onClick;

  /**
   * Renders the value as a link when the identity is clickable, or as
   * plain text otherwise.
   */
  const renderValue = (value: ReactNode) => {
    if (isLink) {
      return (
        <TextLink strong={strong} onClick={onClick} style={{ whiteSpace: "nowrap" }}>
          {value}
        </TextLink>
      );
    }

    return (
      <Text strong={strong} style={{ whiteSpace: "nowrap" }}>
        {value}
      </Text>
    );
  };

  const isGuest = id === GUEST;

  if (isGuest) {
    return renderValue("Guest");
  }

  const isSystem = [ SYSTEM, ROOT ].includes(id);

  if (isSystem) {
    return renderValue("System");
  }

  const identity = accountsMap[id] || brokersMap[id] || membersMap[id];

  if (!identity) {
    if (shouldFallbackToId) {
      return renderValue(id);
    }

    return null;
  }

  const isInvestor = id.startsWith("ACC_");

  const { givenName, additionalName, familyName } = isInvestor
    ? identity.investor
    : identity;

  const fullName = [
    givenName,
    additionalName,
    familyName,
  ].join(" ");

  const { status, isDisabled, disableReason } = identity;

  const roles = isInvestor
    ? [ ROLES.INVESTOR ]
    : identity.roles || [];

  const [ mainRoleId ] = roles;
  const role = ROLES_MAP[mainRoleId as keyof typeof ROLES_MAP];

  if (!role) {
    return <>{children || fullName}</>;
  }

  /**
   * Returns the avatar's background and text colors for the identity's
   * role and status.
   */
  const getAvatarStyles = () =>
    getColors(mainRoleId, role, status, colorBgLayout);

  /**
   * Returns the identity's initials, built from the first letter of each
   * name part.
   */
  const getInitials = () =>
    fullName
      .split(" ")
      .map((word) => word[0])
      .join("");

  return (
    <Space>
      {
        hasAvatar && (
          <Avatar
            size={size}
            style={getAvatarStyles()}
          >
            {getInitials()}
          </Avatar>
        )
      }

      {
        renderValue(children || fullName)
      }

      {
        isDisabled && (
          <Tooltip
            placement="top"
            title={
              disableReason
                ? [ "Account Disabled", disableReason ].join(": ")
                : "Account Disabled"
            }
          >
            <WarningOutlined />
          </Tooltip>
        )
      }
    </Space>
  );
};

export default Identity;
